// Global Profile Completion Service
// This provides a singleton service for managing profile completion state across the app

import axiosInstance from '../config/axios.config';
import { calculateProfileCompletion } from '../utils/profileUtils';

class ProfileCompletionService {
    constructor() {
        this.profileCompletion = null;
        this.loading = true;
        this.listeners = new Set();
        this.initialized = false;
        this.pendingRequest = null; // Track ongoing requests to prevent duplicates
    }

    // Subscribe to profile changes
    subscribe(callback) {
        this.listeners.add(callback);
        // Don't immediately call to avoid re-render issues
        // callback(this.profileCompletion, this.loading);
        
        // Return unsubscribe function
        return () => {
            this.listeners.delete(callback);
        };
    }

    // Notify all listeners of state change
    notifyListeners() {
        this.listeners.forEach(callback => {
            callback(this.profileCompletion, this.loading);
        });
    }

    // Initialize or refresh profile completion
    async fetchProfileCompletion(forceRefresh = false) {
        // If there's already a pending request and not forcing refresh, wait for it silently
        if (this.pendingRequest && !forceRefresh) {
            console.log('⚠️ [ProfileService] Request already in progress, waiting...');
            return this.pendingRequest.then(() => this.profileCompletion).catch(() => this.profileCompletion);
        }

        // If already initialized and not forcing refresh, return cached data
        if (this.initialized && this.profileCompletion && !forceRefresh) {
            console.log('✅ [ProfileService] Using cached profile completion - No API call');
            return this.profileCompletion;
        }

        try {
            console.log('🌐 [ProfileService] Fetching profile completion...', forceRefresh ? '(FORCE REFRESH)' : '');
            console.log('[ProfileService] Auth token present:', !!localStorage.getItem('authToken'));
            this.loading = true;
            this.notifyListeners();

            // Add cache busting for force refresh
            const url = forceRefresh ? '/user-details/profile?_t=' + Date.now() : '/user-details/profile';

            // Create and store the promise
            this.pendingRequest = axiosInstance.get(url);
            const response = await this.pendingRequest;

            console.log('[ProfileService] Profile API Response:', response.data);
            
            if (response.data && response.data.success) {
                const completion = calculateProfileCompletion(response.data.data);
                console.log('[ProfileService] Calculated completion:', completion);
                
                this.profileCompletion = completion;
                this.loading = false;
                
                // Store in localStorage for quick access
                localStorage.setItem('profileCompletion', JSON.stringify(completion));
                
                this.notifyListeners();
                this.initialized = true;
                this.pendingRequest = null; // Clear pending request
                return completion;
            }
        } catch (error) {
            console.error('[ProfileService] Failed to fetch profile:', error);
            this.pendingRequest = null; // Clear pending request on error
            
            // Try to load from localStorage as fallback
            const cached = localStorage.getItem('profileCompletion');
            if (cached) {
                console.log('[ProfileService] Using cached profile completion');
                this.profileCompletion = JSON.parse(cached);
            } else {
                // Default state if no cache
                this.profileCompletion = {
                    percentage: 0,
                    isComplete: false,
                    missingFields: [],
                    sectionStatus: {}
                };
            }
            
            this.loading = false;
            this.notifyListeners();
        }
        
        this.initialized = true;
        return this.profileCompletion;
    }

    // Get current profile completion state
    getProfileCompletion() {
        // If not initialized and we have auth token, fetch it (swallow errors — handled inside fetchProfileCompletion)
        if (!this.initialized && localStorage.getItem('authToken')) {
            this.fetchProfileCompletion().catch(() => {});
        }
        return this.profileCompletion;
    }

    // Check if profile is complete
    isProfileComplete() {
        return this.profileCompletion?.isComplete || false;
    }

    // Get completion percentage
    getCompletionPercentage() {
        return this.profileCompletion?.percentage || 0;
    }

    // Clear profile data (on logout)
    clearProfile() {
        console.log('[ProfileService] Clearing profile data and forcing refresh');
        this.profileCompletion = null;
        this.loading = false;
        this.initialized = false;
        localStorage.removeItem('profileCompletion');
        this.notifyListeners();
    }
    
    // Force refresh profile completion (for when profile is updated)
    async forceRefresh() {
        console.log('[ProfileService] Force refreshing profile completion');
        this.clearProfile();
        return await this.fetchProfileCompletion(true);
    }

    // Debug method to force refresh and show detailed info
    async debugProfileCompletion() {
        console.log('[ProfileService] === DEBUG PROFILE COMPLETION ===');
        try {
            const response = await axiosInstance.get('/user-details/profile');
            console.log('[ProfileService] Raw API Response:', JSON.stringify(response.data, null, 2));
            
            if (response.data?.success) {
                const completion = calculateProfileCompletion(response.data.data);
                console.log('[ProfileService] Calculated completion:', completion);
                console.log('[ProfileService] Section status:', completion.sectionStatus);
                console.log('[ProfileService] Missing fields:', completion.missingFields);
                
                this.profileCompletion = completion;
                this.loading = false;
                this.notifyListeners();
                
                return completion;
            }
        } catch (error) {
            console.error('[ProfileService] Debug failed:', error);
        }
    }

    // Force clear cache and refetch (for debugging)
    forceClearAndRefetch() {
        console.log('[ProfileService] Force clearing cache and refetching...');
        this.clearProfile();
        return this.fetchProfileCompletion();
    }
}

// Create singleton instance
const profileCompletionService = new ProfileCompletionService();

// NOTE: Auto-initialization removed to prevent duplicate API calls
// Profile will be initialized by App.js when user is authenticated
// This reduces from 3 API calls to 1 API call on page load

export default profileCompletionService;